import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';

const ContactSection = () => {
  const { toast } = useToast();
  
  // EmailJS Configuration
  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_fx5ikcr';
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_a5mx55g';
  const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'vW35ftCWXyhejVQyf';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    enquiryType: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      enquiryType: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get current date and time
      const now = new Date();
      const timeString = now.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      // Prepare template parameters for EmailJS
      const templateParams = {
        name: `${formData.firstName} ${formData.lastName}`,
        time: timeString,
        message: formData.message || 'No message provided',
        from_email: formData.email,
        phone: formData.phone,
        enquiry_type: formData.enquiryType,
        to_name: 'BloomCycle Innovations Team',
      };

      // Send email using EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      // Success toast
      toast({
        title: 'Success!',
        description: 'Thank you for your enquiry! We will get back to you soon.',
        variant: 'default',
      });

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        enquiryType: '',
        message: '',
      });
    } catch (error) {
      console.error('EmailJS Error:', error);
      
      // Error toast
      toast({
        title: 'Error',
        description: 'Failed to send your message. Please try again or contact us directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="relative pt-20 pb-20 overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1500937386664-56d1dfef3854?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxmYXJtZXIlMjBmaWVsZHxlbnwwfHx8fDE3NjIzMzc3ODJ8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/70 via-white/65 to-white/70 z-0"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-heading font-bold text-neo-black mb-4">
            Reach out to us
          </h2>
          <p className="text-xl text-neo-gray max-w-3xl mx-auto font-body">
            Have questions about our carbon removal solutions? We'd love to hear from you.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white/90 backdrop-blur-md border-2 border-neo-emerald/20 rounded-2xl shadow-xl p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName" className="text-neo-black font-body mb-2">
                    First name <span className="text-neo-emerald">*</span>
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full border-neo-emerald/30 focus:border-neo-emerald focus:ring-neo-emerald/20"
                    placeholder="Enter your first name"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-neo-black font-body mb-2">
                    Last name <span className="text-neo-emerald">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full border-neo-emerald/30 focus:border-neo-emerald focus:ring-neo-emerald/20"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="text-neo-black font-body mb-2">
                    Email <span className="text-neo-emerald">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full border-neo-emerald/30 focus:border-neo-emerald focus:ring-neo-emerald/20"
                    placeholder="your.email@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-neo-black font-body mb-2">
                    Phone number <span className="text-neo-emerald">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full border-neo-emerald/30 focus:border-neo-emerald focus:ring-neo-emerald/20"
                    placeholder="+91 12345 67890"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="enquiryType" className="text-neo-black font-body mb-2">
                  Enquiry Type <span className="text-neo-emerald">*</span>
                </Label>
                <Select value={formData.enquiryType} onValueChange={handleSelectChange} required>
                  <SelectTrigger className="w-full border-neo-emerald/30 focus:border-neo-emerald focus:ring-neo-emerald/20">
                    <SelectValue placeholder="Select enquiry type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agribusiness">Agribusiness</SelectItem>
                    <SelectItem value="farmer-partner-organisation">Farmer Partner Organisation</SelectItem>
                    <SelectItem value="agroforestry-practitioner">Agroforestry Practitioner</SelectItem>
                    <SelectItem value="carbon-credit-buyer">Carbon Credit Buyer</SelectItem>
                    <SelectItem value="net-zero-inquiry">Net-Zero Inquiry</SelectItem>
                    <SelectItem value="insetting-inquiry">Insetting Inquiry</SelectItem>
                    <SelectItem value="service-provider">Service Provider</SelectItem>
                    <SelectItem value="farmer">Farmer</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-neo-black font-body mb-2">
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="w-full border-neo-emerald/30 focus:border-neo-emerald focus:ring-neo-emerald/20 resize-none"
                  placeholder="Tell us about your enquiry..."
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-neo-emerald hover:bg-[#1db868] text-neo-white py-6 text-lg font-body glow-emerald-hover transition-all duration-300"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;

